import { useCallback, useEffect, useMemo, useState, type JSX } from 'react'
import {
  Network,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  X,
  Save,
  AlertTriangle,
  Bot,
  Eye,
  History as HistoryIcon,
  Archive,
  RotateCcw,
  Search,
  ArrowUp,
  ArrowDown,
  GripVertical,
  Tag,
  AlertCircle,
  FileText,
  Settings,
  Users,
  Sparkles,
  Wand2,
  Check,
  BookOpen,
} from 'lucide-react'
import { api } from '../../../api/client.js'
import { useToast } from '../../system/ToastProvider.js'
import ResourceDrawer from '../ResourceDrawer.js'
import { OrchestratorGraph } from './OrchestratorGraph.js'
import { GuardrailPicker } from './GuardrailPicker.js'
import Select from '../../ui/Select.js'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Suggested intent filter keywords by agent type. */
const INTENT_FILTER_SUGGESTIONS: Record<string, string[]> = {
  routing: ['assign', 'transfer', 'route', 'dispatch', 'forward', 'escalate', 'queue', 'priority', 'agent', 'handover'],
  response: ['help', 'answer', 'question', 'knowledge', 'information', 'explain', 'describe', 'who is', 'tell me about', 'what are'],
  data: ['data', 'report', 'query', 'export', 'csv', 'stats', 'database', 'table', 'count', 'list', 'search', 'how many', 'show me'],
  translation: ['translate', 'translation', 'language', 'convert language'],
  custom: ['custom', 'process', 'handle'],
}

/** Get suggestions for an agent based on its type. */
function getIntentFilterSuggestions(agentType: string): string[] {
  return INTENT_FILTER_SUGGESTIONS[agentType] ?? INTENT_FILTER_SUGGESTIONS.custom ?? []
}

interface AgentOption {
  id: string
  slug: string
  name: string
  type: string
}

interface OrchestratorMember {
  id: string
  agentId: string
  slug: string
  name: string
  type: string
  isEnabled: boolean
  sortOrder: number
  intentFilter: string[]
}

interface Orchestrator {
  id: string
  slug: string
  name: string
  description: string | null
  purpose: string
  /** Custom LLM classification-prompt preamble. Empty = runtime default. */
  prompt: string
  /** Optional shared prompt template ID (mutually exclusive with prompt). */
  promptId: string | null
  type: 'main' | 'sub-orchestrator'
  parentOrchestratorId: string | null
  config: Record<string, unknown> | null
  isSystem: boolean
  isEnabled: boolean
  isArchived?: boolean
  archivedAt?: string | null
  sortOrder: number
  updatedAt: string
  version?: number
  members?: OrchestratorMember[]
}

interface OrchestratorForm {
  slug: string
  name: string
  description: string
  purpose: string
  /** Custom LLM classification-prompt preamble. Empty = runtime default. */
  prompt: string
  /** Optional shared prompt template ID (mutually exclusive with prompt). */
  promptId: string | null
  type: 'main' | 'sub-orchestrator'
  parentOrchestratorId: string
  configPreset: string
  /** Raw config object — preserved on save so agentType isn't wiped. */
  config: Record<string, unknown> | null
  isEnabled: boolean
  sortOrder: number
  changeNote: string
  updatedAt?: string
}

/** AI-generation brief for the orchestrator prompt — same shape as PromptFormDrawer. */
interface GenerateBrief {
  task: string
  audience: string
  tone: 'professional' | 'friendly' | 'concise' | 'detailed'
  constraints: string
  goal: string
  rolePersona: string
  instructions: string
}

function emptyBrief(): GenerateBrief {
  return { task: '', audience: '', tone: 'professional', constraints: '', goal: '', rolePersona: '', instructions: '' }
}

const TONE_OPTIONS: { value: GenerateBrief['tone']; label: string }[] = [
  { value: 'professional', label: 'Professional' },
  { value: 'friendly', label: 'Friendly' },
  { value: 'concise', label: 'Concise' },
  { value: 'detailed', label: 'Detailed' },
]

interface HistoryEntry {
  id: string
  orchestratorId: string
  action: string
  changedBy: string
  changedByName: string
  changedAt: string
  changeNote?: string | null
  diff: Record<string, { before: unknown; after: unknown }>
  snapshot?: Record<string, unknown>
}

interface PaginationMeta {
  total: number
  page: number
  limit: number
  totalPages: number
}

type SideSortField = 'sortOrder' | 'name' | 'updatedAt' | 'createdAt'
type StatusFilter = 'all' | 'enabled' | 'disabled'

const SIDE_PAGE_SIZES = [10, 25, 50] as const

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const emptyForm: OrchestratorForm = {
  slug: '',
  name: '',
  description: '',
  purpose: '',
  prompt: '',
  promptId: null,
  type: 'sub-orchestrator',
  parentOrchestratorId: '',
  configPreset: '',
  config: null,
  isEnabled: true,
  sortOrder: 0,
  changeNote: '',
}

function presetFromConfig(config: Record<string, unknown> | null): string {
  if (config && typeof config.agentType === 'string') return config.agentType
  return ''
}

/** Mirrors the backend zod limits in orchestrators.ts exactly. */
const FIELD_LIMITS = {
  slug: { min: 2, max: 100 },
  name: { min: 1, max: 200 },
  description: { max: 1000 },
  purpose: { min: 1, max: 1000 },
  prompt: { max: 20000 },
} as const

// ---------------------------------------------------------------------------
// Config-time validation warnings (Priority 4)
//
// Mirrors the server-side validateOrchestratorConfig() but works with the
// panel's Orchestrator[] shape instead of OrchestratorConfig. Non-blocking —
// warnings are shown inline but don't prevent saving.
// ---------------------------------------------------------------------------

/** Default minimum keyword length for the short-keyword warning. */
const DEFAULT_MIN_KEYWORD_LENGTH = 4

/** Default denylist of generic keywords that match too broadly. */
const DEFAULT_KEYWORD_DENYLIST = ['help', 'yes', 'ok', 'no', 'hi', 'hey', 'please', 'thanks']

interface ConfigWarning {
  severity: 'warning' | 'info'
  code: 'duplicate_agent_type' | 'short_keyword' | 'denylisted_keyword' | 'client_side_response_agent'
  message: string
  sideSlugs: string[]
  keywords?: string[]
}

/**
 * Validates the orchestrator hierarchy for risky configurations.
 * Pure function — safe to call on every render.
 */
function validateOrchestrators(
  orchestrators: Orchestrator[],
  opts: { minKeywordLength?: number; keywordDenylist?: string[] } = {},
): ConfigWarning[] {
  const minKeywordLength = opts.minKeywordLength ?? DEFAULT_MIN_KEYWORD_LENGTH
  const denylist = new Set((opts.keywordDenylist ?? DEFAULT_KEYWORD_DENYLIST).map((k) => k.toLowerCase()))
  const warnings: ConfigWarning[] = []

  const enabledSides = orchestrators.filter((o) => o.type === 'sub-orchestrator' && o.isEnabled && !o.isArchived)

  // 0. Response agent on the client-facing sub-orchestrator. The general
  // response agent does open-ended RAG over the tenant's knowledge
  // collections (SOPs and other staff-facing content) and serves
  // agent-assist training Q&A — all reachable by anonymous client-portal
  // visitors when it's a member of the agentType:"client" sub-orchestrator.
  for (const side of enabledSides) {
    if (side.config?.agentType !== 'client') continue
    const responseMembers = (side.members ?? [])
      .filter((m) => m.type === 'response')
      .map((m) => m.slug)
    if (responseMembers.length > 0) {
      warnings.push({
        severity: 'warning',
        code: 'client_side_response_agent',
        message:
          `Side "${side.slug}" is client-facing (agentType "client") but has response-type agent(s): ` +
          `${responseMembers.join(', ')}. The general response agent is reachable by anonymous client-portal ` +
          `visitors and can surface internal knowledge-base content (SOPs) and agent-assist training Q&A in ` +
          `its answers. Remove the response agent from this side and route client-portal traffic through the ` +
          `FAQ agent only.`,
        sideSlugs: [side.slug],
      })
    }
  }

  // 1. Duplicate agentType across enabled sides.
  const typeToSides = new Map<string, string[]>()
  for (const side of enabledSides) {
    const agentType = side.config?.agentType
    if (typeof agentType !== 'string' || !agentType) continue
    const existing = typeToSides.get(agentType) ?? []
    existing.push(side.slug)
    typeToSides.set(agentType, existing)
  }
  for (const [agentType, sideSlugs] of typeToSides) {
    if (sideSlugs.length > 1) {
      warnings.push({
        severity: 'warning',
        code: 'duplicate_agent_type',
        message:
          `Multiple enabled sub-orchestrators share the agentType "${agentType}": ${sideSlugs.join(', ')}. ` +
          `The first in sort order will win for LLM-classified routing, which may not match your intent. ` +
          `Consider giving each sub-orchestrator a unique agentType or disabling the duplicates.`,
        sideSlugs,
      })
    }
  }

  // 2 & 3. Short and denylisted keywords in member intent filters.
  for (const side of enabledSides) {
    const shortKeywords: string[] = []
    const denylistedKeywords: string[] = []
    for (const member of side.members ?? []) {
      for (const keyword of member.intentFilter) {
        const kw = keyword.toLowerCase().trim()
        if (!kw) continue
        if (kw.length < minKeywordLength) shortKeywords.push(kw)
        if (denylist.has(kw)) denylistedKeywords.push(kw)
      }
    }
    if (shortKeywords.length > 0) {
      warnings.push({
        severity: 'warning',
        code: 'short_keyword',
        message:
          `Side "${side.slug}" has short intent-filter keywords (${shortKeywords.join(', ')}). ` +
          `Keywords shorter than ${minKeywordLength} characters match too broadly and may cause false-positive routes. ` +
          `Consider using longer, more specific phrases.`,
        sideSlugs: [side.slug],
        keywords: shortKeywords,
      })
    }
    if (denylistedKeywords.length > 0) {
      warnings.push({
        severity: 'warning',
        code: 'denylisted_keyword',
        message:
          `Side "${side.slug}" uses generic denylisted keywords (${denylistedKeywords.join(', ')}). ` +
          `These match far too many unrelated messages to be safe routing shortcuts. ` +
          `Consider removing them or using more specific phrases.`,
        sideSlugs: [side.slug],
        keywords: denylistedKeywords,
      })
    }
  }

  return warnings
}

/**
 * Pure client-side validation matching the backend schema, so an over-limit
 * field disables Save instead of costing a failed API round-trip.
 */
function validateForm(form: OrchestratorForm, isEditing: boolean): Record<string, string> {
  const errors: Record<string, string> = {}
  if (form.slug.length < FIELD_LIMITS.slug.min || form.slug.length > FIELD_LIMITS.slug.max) {
    errors.slug = `Slug is required (${FIELD_LIMITS.slug.min}-${FIELD_LIMITS.slug.max} characters)`
  }
  if (form.name.length < FIELD_LIMITS.name.min) {
    errors.name = 'Name is required'
  } else if (form.name.length > FIELD_LIMITS.name.max) {
    errors.name = `Name exceeds the ${FIELD_LIMITS.name.max} character limit`
  }
  if (form.description.length > FIELD_LIMITS.description.max) {
    errors.description = `Description exceeds the ${FIELD_LIMITS.description.max} character limit`
  }
  // Purpose is required when creating; when editing, only validate length if
  // the user has entered a value (existing orchestrators may have empty purpose
  // from before the field was added).
  if (!isEditing && form.purpose.length < FIELD_LIMITS.purpose.min) {
    errors.purpose = 'Purpose is required'
  }
  if (form.purpose.length > FIELD_LIMITS.purpose.max) {
    errors.purpose = `Purpose exceeds the ${FIELD_LIMITS.purpose.max} character limit`
  }
  if (form.prompt.length > FIELD_LIMITS.prompt.max) {
    errors.prompt = `Prompt exceeds the ${FIELD_LIMITS.prompt.max} character limit`
  }
  return errors
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
}

function inputClass(error = false, disabled = false): string {
  const base = 'w-full rounded-lg border px-3 py-2 text-sm outline-none transition-colors'
  if (disabled) {
    return `${base} border-gray-200 bg-gray-100 text-gray-500 cursor-not-allowed`
  }
  return `${base} bg-white text-gray-700 focus:ring-2 ${
    error
      ? 'border-red-300 focus:border-red-500 focus:ring-red-100'
      : 'border-gray-200 focus:border-gray-400 focus:ring-gray-100'
  }`
}

const labelClass = 'mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-400'

// ---------------------------------------------------------------------------
// Component: FieldLabel
// ---------------------------------------------------------------------------

/** Label plus a character counter that turns red once the limit is exceeded. */
function FieldLabel({ label, value, max }: { label: string; value: string; max: number }): JSX.Element {
  const over = value.length > max
  return (
    <div className="flex items-center justify-between">
      <label className={labelClass}>{label}</label>
      <span className={`text-[10px] ${over ? 'font-semibold text-red-500' : 'text-gray-400'}`}>
        {value.length}/{max}
      </span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Component: TagInput
// ---------------------------------------------------------------------------

function TagInput({
  tags,
  onChange,
  placeholder = 'Type intent and press Enter...',
  suggestions = [],
}: {
  tags: string[]
  onChange: (tags: string[]) => void
  placeholder?: string
  suggestions?: string[]
}): JSX.Element {
  const [input, setInput] = useState('')

  const addTag = (val: string) => {
    const trimmed = val.trim().toLowerCase()
    if (trimmed && !tags.includes(trimmed)) {
      onChange([...tags, trimmed])
    }
    setInput('')
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      addTag(input)
    } else if (e.key === 'Backspace' && !input && tags.length > 0) {
      onChange(tags.slice(0, tags.length - 1))
    }
  }

  const removeTag = (index: number) => {
    onChange(tags.filter((_, i) => i !== index))
  }

  // Show suggestions that aren't already added
  const availableSuggestions = suggestions.filter((s) => !tags.includes(s.toLowerCase()))

  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-center gap-1 rounded-sm border border-gray-300 bg-white p-1 text-xs focus-within:border-[#3454cf] focus-within:ring-1 focus-within:ring-[#3454cf]">
        {tags.map((tag, i) => (
          <span key={i} className="inline-flex items-center gap-1 rounded bg-purple-50 px-2 py-0.5 text-[11px] font-medium text-purple-700">
            <Tag className="h-2.5 w-2.5" />
            {tag}
            <button type="button" onClick={() => removeTag(i)} className="text-purple-400 hover:text-purple-700">
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => input && addTag(input)}
          placeholder={tags.length === 0 ? placeholder : ''}
          className="min-w-[100px] flex-1 bg-transparent px-1 py-0.5 outline-none placeholder:text-gray-400"
        />
      </div>
      {availableSuggestions.length > 0 && (
        <div className="flex flex-wrap items-center gap-1">
          <span className="text-[9px] text-gray-400">Suggestions:</span>
          {availableSuggestions.slice(0, 8).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => addTag(s)}
              className="inline-flex items-center gap-0.5 rounded-sm border border-dashed border-gray-300 bg-gray-50 px-1.5 py-0.5 text-[10px] text-gray-500 hover:border-[#3454cf] hover:bg-blue-50/40 hover:text-[#3454cf]"
            >
              <Plus className="h-2.5 w-2.5" />
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Component: Main OrchestratorsPanel
// ---------------------------------------------------------------------------

/** Sortable column header for the side-orchestrator table. */
function SideSortTh({
  label,
  field,
  sortBy,
  sortOrder,
  onSort,
  align = 'left',
}: {
  label: string
  field: SideSortField
  sortBy: SideSortField
  sortOrder: 'asc' | 'desc'
  onSort: (field: SideSortField) => void
  align?: 'left' | 'right'
}): JSX.Element {
  const isActive = sortBy === field
  const indicator = isActive ? (sortOrder === 'asc' ? ' ▲' : ' ▼') : ''
  return (
    <th
      className={`cursor-pointer select-none px-4 py-2 font-medium hover:text-gray-700 ${
        align === 'right' ? 'text-right' : 'text-left'
      }`}
      onClick={() => onSort(field)}
    >
      {label}
      <span className="ml-1 text-[10px] opacity-60">{indicator}</span>
    </th>
  )
}

export default function OrchestratorsPanel(): JSX.Element {
  const toast = useToast()

  // Full, unpaginated list — needed for the main card, graph preview,
  // parent-orchestrator picker and delete-safety child checks.
  const [allOrchestrators, setAllOrchestrators] = useState<Orchestrator[]>([])

  // Paginated slice of side orchestrators rendered in the table.
  const [pagedSides, setPagedSides] = useState<Orchestrator[]>([])
  const [pagination, setPagination] = useState<PaginationMeta>({
    total: 0,
    page: 1,
    limit: SIDE_PAGE_SIZES[0],
    totalPages: 1,
  })

  const [agents, setAgents] = useState<AgentOption[]>([])
  const [loading, setLoading] = useState(true)
  const [showArchived, setShowArchived] = useState(false)

  // Table query state (server-side).
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState<number>(SIDE_PAGE_SIZES[0])
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [sortBy, setSortBy] = useState<SideSortField>('createdAt')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing, setEditing] = useState<Orchestrator | null>(null)
  const [previewOpen, setPreviewOpen] = useState(false)

  // History & Delete Safety state
  const [historyDrawerOrchestrator, setHistoryDrawerOrchestrator] = useState<Orchestrator | null>(null)
  const [deletingOrchestrator, setDeletingOrchestrator] = useState<Orchestrator | null>(null)
  const [archivingOrchestrator, setArchivingOrchestrator] = useState<Orchestrator | null>(null)

  const loadAllOrchestrators = useCallback(async () => {
    try {
      const data = await api.get<{ orchestrators: Orchestrator[]; pagination?: PaginationMeta }>(
        `/admin/ai/orchestrators?includeArchived=${showArchived}&all=true`,
      )
      setAllOrchestrators(data.orchestrators ?? [])
    } catch (err) {
      toast.error('Failed to load orchestrators:', err instanceof Error ? err.message : String(err))
    }
  }, [showArchived, toast])

  const loadPagedSides = useCallback(async () => {
    try {
      setLoading(true)
      const params = new URLSearchParams({
        includeArchived: String(showArchived),
        type: 'sub-orchestrator',
        search,
        status: statusFilter,
        sortBy,
        sortOrder,
        page: String(page),
        limit: String(limit),
      })
      const data = await api.get<{ orchestrators: Orchestrator[]; pagination: PaginationMeta }>(
        `/admin/ai/orchestrators?${params.toString()}`,
      )
      setPagedSides(data.orchestrators ?? [])
      setPagination(data.pagination)
    } catch (err) {
      toast.error('Failed to load orchestrators:', err instanceof Error ? err.message : String(err))
    } finally {
      setLoading(false)
    }
  }, [showArchived, search, statusFilter, sortBy, sortOrder, page, limit, toast])

  const loadAgents = useCallback(async () => {
    try {
      const data = await api.get<{ agents: AgentOption[] }>('/admin/ai/agents')
      setAgents(data.agents ?? [])
    } catch (err) {
      toast.error('Failed to load agents:', err instanceof Error ? err.message : String(err))
    }
  }, [toast])

  const reload = useCallback(async () => {
    await Promise.all([loadAllOrchestrators(), loadPagedSides()])
  }, [loadAllOrchestrators, loadPagedSides])

  useEffect(() => {
    loadAllOrchestrators()
    loadAgents()
  }, [loadAllOrchestrators, loadAgents])

  useEffect(() => {
    void loadPagedSides()
  }, [loadPagedSides])

  // Debounce the search box; reset to the first page whenever the query changes.
  useEffect(() => {
    const handle = setTimeout(() => {
      setSearch((prev) => (prev === searchInput ? prev : searchInput))
      setPage(1)
    }, 300)
    return () => clearTimeout(handle)
  }, [searchInput])

  const changeStatusFilter = (next: StatusFilter): void => {
    setStatusFilter(next)
    setPage(1)
  }

  const changeLimit = (next: number): void => {
    setLimit(next)
    setPage(1)
  }

  const changeSort = (field: SideSortField): void => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(field)
      // Date fields default to desc (newest first); others default to asc.
      setSortOrder(field === 'createdAt' || field === 'updatedAt' ? 'desc' : 'asc')
    }
    setPage(1)
  }

  const toggleShowArchived = (checked: boolean): void => {
    setShowArchived(checked)
    setPage(1)
  }

  const goToPage = (next: number): void => {
    const clamped = Math.min(Math.max(1, next), pagination.totalPages)
    setPage(clamped)
  }

  const handleToggle = async (orchestrator: Orchestrator): Promise<void> => {
    try {
      await api.put(`/admin/ai/orchestrators/${orchestrator.slug}`, {
        isEnabled: !orchestrator.isEnabled,
        updatedAt: orchestrator.updatedAt,
      })
      toast.success(`Orchestrator ${orchestrator.isEnabled ? 'disabled' : 'enabled'}`)
      await reload()
    } catch (err) {
      toast.error('Failed to update orchestrator:', err instanceof Error ? err.message : String(err))
    }
  }

  const handleUnarchive = async (orchestrator: Orchestrator): Promise<void> => {
    try {
      await api.post(`/admin/ai/orchestrators/${orchestrator.slug}/unarchive`, {})
      toast.success('Orchestrator restored', 'It was restored disabled — enable it when ready.')
      await reload()
    } catch (err) {
      const errObj = err as { data?: { error?: string; message?: string } }
      toast.error('Failed to restore orchestrator:', err instanceof Error ? err.message : String(errObj.data?.message ?? err))
    }
  }

  const handleArchive = async (orchestrator: Orchestrator): Promise<void> => {
    try {
      await api.post(`/admin/ai/orchestrators/${orchestrator.slug}/archive`, {})
      toast.success('Orchestrator archived', 'Enable "Show Archived" to find or restore it.')
      setArchivingOrchestrator(null)
      await reload()
    } catch (err) {
      const errObj = err as { data?: { message?: string } }
      toast.error(
        'Failed to archive orchestrator:',
        errObj.data?.message ?? (err instanceof Error ? err.message : String(err)),
      )
    }
  }

  const main = useMemo(() => allOrchestrators.find((o) => o.type === 'main' && !o.isArchived), [allOrchestrators])
  const mains = useMemo(
    () => allOrchestrators.filter((o) => o.type === 'main' && !o.isArchived),
    [allOrchestrators],
  )
  const graphSides = useMemo(
    () =>
      allOrchestrators
        .filter((o) => o.type === 'sub-orchestrator')
        .sort((a, b) => a.sortOrder - b.sortOrder),
    [allOrchestrators],
  )

  // Config-time validation warnings — non-blocking, computed from the full
  // hierarchy so cross-side issues (e.g. duplicate agentTypes) are caught.
  const configWarnings = useMemo(
    () => validateOrchestrators(allOrchestrators),
    [allOrchestrators],
  )

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Orchestrators</h3>
          <p className="text-xs text-gray-500">Manage hierarchical main & sub-orchestrators and agent dispatch</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex cursor-pointer items-center gap-1.5 rounded-sm border border-gray-200 bg-white px-2.5 py-1.5 text-xs text-gray-700 hover:bg-gray-50">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => toggleShowArchived(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-gray-300 text-[#3454cf] focus:ring-[#3454cf]"
            />
            <span>Show Archived</span>
          </label>

          <button
            className="inline-flex items-center gap-1.5 rounded-sm border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50"
            onClick={() => setPreviewOpen(true)}
          >
            <Eye className="h-3.5 w-3.5" />
            Preview
          </button>
          <button
            className="inline-flex items-center gap-1.5 rounded-sm bg-[#3454cf] px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#2a45b0]"
            onClick={() => {
              setEditing(null)
              setDrawerOpen(true)
            }}
          >
            <Plus className="h-3.5 w-3.5" />
            Create Orchestrator
          </button>
        </div>
      </div>

      {main && (
        <div className={`rounded-sm border p-4 shadow-[0_2px_12px_rgba(0,0,0,0.04)] ${main.isArchived ? 'border-amber-200 bg-amber-50/40' : 'border-gray-200 bg-white'}`}>
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Network className="h-4 w-4 text-[#3454cf]" />
              <h4 className="text-sm font-semibold text-gray-900">{main.name}</h4>
              <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700">Main</span>
              {main.isSystem && (
                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-600">System</span>
              )}
              {main.isArchived && (
                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800">Archived</span>
              )}
            </div>
            <div className="flex items-center gap-1">
              <button
                className="rounded-sm p-1.5 text-gray-500 hover:bg-gray-100"
                onClick={() => setHistoryDrawerOrchestrator(main)}
                title="Audit History"
              >
                <HistoryIcon className="h-3.5 w-3.5" />
              </button>
              <button
                className="rounded-sm p-1.5 text-gray-500 hover:bg-gray-100"
                onClick={() => {
                  setEditing(main)
                  setDrawerOpen(true)
                }}
                title="Edit main orchestrator"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
          <p className="text-xs text-gray-500">{main.description ?? 'Top-level orchestrator'}</p>
        </div>
      )}

      {configWarnings.length > 0 && (
        <div className="space-y-2 rounded-sm border border-amber-200 bg-amber-50/50 p-3">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-800">
            <AlertTriangle className="h-3.5 w-3.5" />
            Configuration Warnings ({configWarnings.length})
          </div>
          <ul className="space-y-1.5">
            {configWarnings.map((w, i) => (
              <li key={`${w.code}-${i}`} className="flex items-start gap-1.5 text-[11px] text-amber-900">
                <AlertCircle className="mt-0.5 h-3 w-3 shrink-0 text-amber-600" />
                <span>{w.message}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="overflow-hidden rounded-sm border border-gray-200 bg-white shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
        {/* Toolbar: search, status filter, page-size selector */}
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-4 py-2.5">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by name or slug..."
              className="w-64 rounded-sm border border-gray-300 bg-white py-1.5 pl-8 pr-3 text-xs text-gray-700 focus:border-[#3454cf] focus:outline-none focus:ring-1 focus:ring-[#3454cf]"
            />
          </div>
          <div className="flex items-center gap-2">
            <Select
              ariaLabel="Status filter"
              size="sm"
              value={statusFilter}
              onChange={(v) => changeStatusFilter(v as StatusFilter)}
            >
              <option value="all">All statuses</option>
              <option value="enabled">Enabled</option>
              <option value="disabled">Disabled</option>
            </Select>
            <Select
              ariaLabel="Rows per page"
              size="sm"
              value={String(limit)}
              onChange={(v) => changeLimit(Number.parseInt(v, 10))}
              options={SIDE_PAGE_SIZES.map((size) => ({ value: String(size), label: `${size} / page` }))}
              className="w-[96px]"
            />
          </div>
        </div>

        <table className="w-full text-left text-xs">
          <thead className="bg-gray-50 text-gray-500">
            <tr>
              <SideSortTh label="Sub-Orchestrator" field="name" sortBy={sortBy} sortOrder={sortOrder} onSort={changeSort} />
              <th className="px-4 py-2 font-medium">Agents</th>
              <th className="px-4 py-2 font-medium">Status</th>
              <SideSortTh label="Order" field="sortOrder" sortBy={sortBy} sortOrder={sortOrder} onSort={changeSort} />
              <th className="px-4 py-2 font-medium">Version</th>
              <SideSortTh label="Updated" field="updatedAt" sortBy={sortBy} sortOrder={sortOrder} onSort={changeSort} />
              <th className="px-4 py-2 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr>
                <td className="px-4 py-6 text-center text-xs text-gray-400" colSpan={7}>
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading...
                  </span>
                </td>
              </tr>
            ) : (
              <>
                {pagedSides.map((side) => (
                  <tr key={side.id} className={`hover:bg-gray-50/50 ${side.isArchived ? 'bg-amber-50/30' : ''}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Bot className="h-3.5 w-3.5 text-purple-500" />
                        <div>
                          <div className="font-medium text-gray-900 flex items-center gap-1.5">
                            {side.name}
                            {side.isArchived && (
                              <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[9px] font-semibold text-amber-800">
                                Archived
                              </span>
                            )}
                          </div>
                          <div className="text-[10px] text-gray-400">{side.slug}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{(side.members?.length ?? 0)} agents</td>
                    <td className="px-4 py-3">
                      <button
                        disabled={side.isArchived}
                        className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                          side.isEnabled ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'
                        } disabled:opacity-50`}
                        onClick={() => handleToggle(side)}
                      >
                        {side.isEnabled ? 'Enabled' : 'Disabled'}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{side.sortOrder}</td>
                    <td className="px-4 py-3 text-gray-600">
                      <span className="inline-flex items-center rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600">
                        v{side.version ?? 0}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-500">
                      {new Date(side.updatedAt).toLocaleDateString(undefined, {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          className="rounded-sm p-1.5 text-gray-500 hover:bg-gray-100"
                          onClick={() => setHistoryDrawerOrchestrator(side)}
                          title="Audit History"
                        >
                          <HistoryIcon className="h-3.5 w-3.5" />
                        </button>
                        {side.isArchived ? (
                          <button
                            className="rounded-sm p-1.5 text-emerald-600 hover:bg-emerald-50"
                            onClick={() => void handleUnarchive(side)}
                            title="Restore from archive"
                          >
                            <RotateCcw className="h-3.5 w-3.5" />
                          </button>
                        ) : (
                          <button
                            className="rounded-sm p-1.5 text-gray-500 hover:bg-gray-100"
                            onClick={() => {
                              setEditing(side)
                              setDrawerOpen(true)
                            }}
                            title="Edit"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {!side.isSystem && !side.isArchived && (
                          <>
                            <button
                              className="rounded-sm p-1.5 text-amber-600 hover:bg-amber-50"
                              onClick={() => setArchivingOrchestrator(side)}
                              title="Archive (reversible)"
                            >
                              <Archive className="h-3.5 w-3.5" />
                            </button>
                            <button
                              className="rounded-sm p-1.5 text-red-500 hover:bg-red-50"
                              onClick={() => setDeletingOrchestrator(side)}
                              title="Delete (permanent)"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {pagedSides.length === 0 && (
                  <tr>
                    <td className="px-4 py-6 text-center text-xs text-gray-400" colSpan={7}>
                      No sub-orchestrators match the current filters.
                    </td>
                  </tr>
                )}
              </>
            )}
          </tbody>
        </table>

        {/* Pagination footer */}
        {pagination.total > 0 && (
          <div className="flex items-center justify-between border-t border-gray-100 px-4 py-2.5 text-xs text-gray-500">
            <div>
              Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
              {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} side
              orchestrators
            </div>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => goToPage(pagination.page - 1)}
                disabled={pagination.page <= 1}
                className="rounded-sm border border-gray-200 px-2.5 py-1 font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Previous
              </button>
              <span className="px-1.5 text-gray-600">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <button
                type="button"
                onClick={() => goToPage(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages}
                className="rounded-sm border border-gray-200 px-2.5 py-1 font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {main && (
        <ResourceDrawer
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          title="Orchestrator Preview"
          width="full"
        >
          <OrchestratorGraph
            main={main}
            sides={graphSides.map((s) => ({ ...s, members: s.members ?? [] }))}
          />
        </ResourceDrawer>
      )}

      <OrchestratorDrawer
        isOpen={drawerOpen}
        editing={editing}
        agents={agents}
        orchestrators={allOrchestrators}
        mains={mains}
        onClose={() => setDrawerOpen(false)}
        onSaved={async () => {
          setDrawerOpen(false)
          await reload()
        }}
      />

      <OrchestratorHistoryDrawerModal
        isOpen={!!historyDrawerOrchestrator}
        orchestrator={historyDrawerOrchestrator}
        onClose={() => setHistoryDrawerOrchestrator(null)}
        onRestored={async () => {
          setHistoryDrawerOrchestrator(null)
          await reload()
        }}
      />

      {archivingOrchestrator && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
            <div className="mb-3 flex items-center gap-2 text-amber-600">
              <Archive className="h-5 w-5" />
              <h3 className="text-sm font-bold text-gray-900">Archive Orchestrator</h3>
            </div>
            <p className="mb-3 text-xs leading-relaxed text-gray-600">
              <strong>{archivingOrchestrator.name}</strong> will be disabled and hidden from the list. Its
              configuration, agent assignments and audit history are kept, and you can restore it at any time
              via <span className="font-medium">Show Archived</span>.
            </p>
            <p className="mb-4 text-[11px] text-gray-500">
              Archiving is reversible — use Delete only to remove an orchestrator permanently.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setArchivingOrchestrator(null)}
                className="rounded border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => void handleArchive(archivingOrchestrator)}
                className="inline-flex items-center gap-1.5 rounded bg-amber-600 px-4 py-1.5 text-xs font-semibold text-white hover:bg-amber-700"
              >
                <Archive className="h-3.5 w-3.5" />
                Archive
              </button>
            </div>
          </div>
        </div>
      )}

      <DeleteSafetyModal
        isOpen={!!deletingOrchestrator}
        orchestrator={deletingOrchestrator}
        orchestrators={allOrchestrators}
        onClose={() => setDeletingOrchestrator(null)}
        onDeleted={async () => {
          setDeletingOrchestrator(null)
          await reload()
        }}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Drawer: Orchestrator Form
// ---------------------------------------------------------------------------

interface OrchestratorDrawerProps {
  isOpen: boolean
  editing: Orchestrator | null
  agents: AgentOption[]
  orchestrators: Orchestrator[]
  mains: Orchestrator[]
  onClose: () => void
  onSaved: () => void | Promise<void>
}

function OrchestratorDrawer({
  isOpen,
  editing,
  agents,
  orchestrators,
  mains,
  onClose,
  onSaved,
}: OrchestratorDrawerProps): JSX.Element {
  const toast = useToast()
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState<OrchestratorForm>(emptyForm)
  const [members, setMembers] = useState<OrchestratorMember[]>([])
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isSlugDirty, setIsSlugDirty] = useState(false)

  // Dirty-state tracking — snapshots of the initial values when the drawer
  // opens, so we can compare and disable Save when nothing has changed.
  const [initialForm, setInitialForm] = useState<OrchestratorForm>(emptyForm)
  const [initialMembers, setInitialMembers] = useState<OrchestratorMember[]>([])

  // Tab navigation — Details → Prompt → Agents. The agents tab depends on
  // type being set, so the tab order enforces a natural fill-in sequence.
  const [activeTab, setActiveTab] = useState<'details' | 'prompt' | 'agents'>('details')

  // ── AI prompt generation (mirrors PromptFormDrawer / EditAgentDrawer) ──
  const [showGenerate, setShowGenerate] = useState(false)
  const [genBrief, setGenBrief] = useState<GenerateBrief>(() => emptyBrief())
  const [isGenerating, setIsGenerating] = useState(false)
  const [isPolishing, setIsPolishing] = useState(false)
  const [polishPreview, setPolishPreview] = useState<{ before: string; after: string } | null>(null)
  // Guardrails selected for inclusion when generating an inline prompt with AI.
  const [selectedGuardrailIds, setSelectedGuardrailIds] = useState<Set<string>>(new Set())
  const [selectedGuardrailContents, setSelectedGuardrailContents] = useState<string[]>([])

  // Member drag-and-drop reordering state
  const [dragIndex, setDragIndex] = useState<number | null>(null)
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)

  // ── Prompt source toggle (same pattern as EditAgentDrawer) ──
  // 'inline' = type the classification prompt directly;
  // 'template' = pick a shared prompt template from Prompt Studio.
  type PromptSource = 'inline' | 'template'
  const [promptSource, setPromptSource] = useState<PromptSource>('inline')
  const [availablePrompts, setAvailablePrompts] = useState<{ id: string; slug: string; name: string; content: string; category: string }[]>([])
  const [promptSearch, setPromptSearch] = useState('')
  const [pendingSourceChange, setPendingSourceChange] = useState<{
    source: PromptSource
    discarding: string
    apply: () => void
  } | null>(null)
  const [pendingPromptChange, setPendingPromptChange] = useState<{
    promptId: string
    previousName: string
    nextName: string | null
  } | null>(null)
  const [isSavingAsPrompt, setIsSavingAsPrompt] = useState(false)

  // Conflict modal state
  const [conflictServerRecord, setConflictServerRecord] = useState<Orchestrator | null>(null)
  // Demotion modal state
  const [showDemoteConfirm, setShowDemoteConfirm] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    setIsSlugDirty(false)
    setConflictServerRecord(null)
    setShowDemoteConfirm(false)
    setActiveTab('details')
    setShowGenerate(false)
    setGenBrief(emptyBrief())
    setPolishPreview(null)
    setSelectedGuardrailIds(new Set())
    setSelectedGuardrailContents([])
    setPromptSearch('')
    setPendingSourceChange(null)
    setPendingPromptChange(null)

    if (editing) {
      setPromptSource(editing.promptId ? 'template' : 'inline')
      const initialEditForm: OrchestratorForm = {
        slug: editing.slug,
        name: editing.name,
        description: editing.description ?? '',
        purpose: editing.purpose ?? '',
        prompt: editing.prompt ?? '',
        promptId: editing.promptId ?? null,
        type: editing.type,
        parentOrchestratorId: editing.parentOrchestratorId ?? '',
        configPreset: '',
        config: editing.config ?? null,
        isEnabled: editing.isEnabled,
        sortOrder: editing.sortOrder,
        changeNote: '',
        updatedAt: editing.updatedAt,
      }
      setForm(initialEditForm)
      setInitialForm(initialEditForm)
      setMembers([])
      setInitialMembers([])
      if (editing.slug) {
        api
          .get<{ orchestrator: Orchestrator & { members: OrchestratorMember[] } }>(`/admin/ai/orchestrators/${editing.slug}`)
          .then((data) => {
            const o = data.orchestrator
            const freshForm: OrchestratorForm = {
              slug: o.slug,
              name: o.name,
              description: o.description ?? '',
              purpose: o.purpose ?? '',
              prompt: o.prompt ?? '',
              promptId: o.promptId ?? null,
              type: o.type,
              parentOrchestratorId: o.parentOrchestratorId ?? '',
              configPreset: presetFromConfig(o.config),
              config: o.config ?? null,
              isEnabled: o.isEnabled,
              sortOrder: o.sortOrder,
              changeNote: '',
              updatedAt: o.updatedAt,
            }
            setForm(freshForm)
            setInitialForm(freshForm)
            setPromptSource(freshForm.promptId ? 'template' : 'inline')
            const freshMembers = o.members ?? []
            setMembers(freshMembers)
            setInitialMembers(freshMembers)
          })
          .catch((err) => toast.error('Failed to load orchestrator:', err instanceof Error ? err.message : String(err)))
      }
    } else {
      setForm(emptyForm)
      setInitialForm(emptyForm)
      setMembers([])
      setInitialMembers([])
    }
    setErrors({})
  }, [isOpen, editing, toast])

  // Load available prompt templates when the drawer opens (for the template picker)
  useEffect(() => {
    if (isOpen && availablePrompts.length === 0) {
      api.get<{ prompts: { id: string; slug: string; name: string; content: string; category: string }[] }>('/admin/ai/prompts').then((res) => {
        setAvailablePrompts(res.prompts ?? [])
      }).catch(() => {})
    }
  }, [isOpen, availablePrompts.length])

  // Filtered prompt templates for the template picker
  const filteredPrompts = useMemo(() => {
    const q = promptSearch.trim().toLowerCase()
    return availablePrompts.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.slug.toLowerCase().includes(q) ||
        p.content.toLowerCase().includes(q),
    )
  }, [availablePrompts, promptSearch])

  /**
   * Switch between inline and template prompt sources. Same confirmation
   * pattern as EditAgentDrawer: the inactive source is cleared so a stale
   * value can't compete at runtime, but the user is warned first whenever
   * that would throw away their work.
   */
  const handlePromptSourceChange = (nextSource: PromptSource): void => {
    if (nextSource === promptSource) return

    const losingInlineText = nextSource === 'template' && form.prompt.trim().length > 0
    const losingTemplate = nextSource === 'inline' && !!form.promptId

    const applySwitch = (): void => {
      setPromptSource(nextSource)
      if (nextSource === 'inline') {
        setForm((prev) => ({ ...prev, promptId: null }))
      } else {
        setForm((prev) => ({ ...prev, prompt: '' }))
      }
    }

    if (!losingInlineText && !losingTemplate) {
      applySwitch()
      return
    }

    setPendingSourceChange({
      source: nextSource,
      discarding: losingInlineText
        ? 'the inline prompt text you typed'
        : `the assigned template "${availablePrompts.find((p) => p.id === form.promptId)?.name ?? 'template'}"`,
      apply: applySwitch,
    })
  }

  /**
   * An orchestrator uses exactly one prompt template, so swapping the
   * selection discards the previous one — confirm before replacing it.
   */
  const handlePromptSelect = (nextPromptId: string): void => {
    const previous = availablePrompts.find((p) => p.id === form.promptId)
    if (!previous || nextPromptId === form.promptId) {
      setForm((prev) => ({ ...prev, promptId: nextPromptId || null }))
      return
    }

    const next = availablePrompts.find((p) => p.id === nextPromptId)
    setPendingPromptChange({
      promptId: nextPromptId,
      previousName: previous.name,
      nextName: next?.name ?? null,
    })
  }

  const handleNameChange = (val: string) => {
    setForm((prev) => {
      const next = { ...prev, name: val }
      if (!editing && !isSlugDirty) {
        next.slug = slugify(val)
      }
      return next
    })
  }

  const handleGenerate = async (): Promise<void> => {
    if (!genBrief.task.trim()) {
      toast.error('Task is required', 'Describe what the orchestrator prompt should accomplish.')
      return
    }
    setIsGenerating(true)
    try {
      const res = await api.post<{ content: string }>('/admin/ai/orchestrators/generate-prompt', {
        task: genBrief.task.trim(),
        ...(genBrief.audience.trim() ? { audience: genBrief.audience.trim() } : {}),
        tone: genBrief.tone,
        guardrailContents: selectedGuardrailContents,
        orchestratorType: form.type,
        ...(form.name.trim() ? { name: form.name.trim() } : {}),
        ...(form.description.trim() ? { description: form.description.trim() } : {}),
        ...(typeof form.config?.agentType === 'string' ? { agentType: form.config.agentType } : {}),
        ...(form.purpose.trim() ? { purpose: form.purpose.trim() } : {}),
        memberAgentNames: members.map((m) => m.name).filter(Boolean),
      })
      setForm((prev) => ({ ...prev, prompt: res.content }))
      setShowGenerate(false)
      toast.success('Prompt generated', 'Review and edit the generated content below.')
    } catch (err) {
      toast.error('Generation failed', err instanceof Error ? err.message : String(err))
    } finally {
      setIsGenerating(false)
    }
  }

  const handlePolish = async (): Promise<void> => {
    if (!form.prompt.trim()) {
      toast.error('Nothing to polish', 'Write or generate some prompt content first.')
      return
    }
    setIsPolishing(true)
    try {
      const res = await api.post<{ content: string }>('/admin/ai/orchestrators/polish-prompt', {
        content: form.prompt,
      })
      setPolishPreview({ before: form.prompt, after: res.content })
    } catch (err) {
      toast.error('Polish failed', err instanceof Error ? err.message : String(err))
    } finally {
      setIsPolishing(false)
    }
  }

  const handleAcceptPolish = (): void => {
    if (polishPreview) {
      setForm((prev) => ({ ...prev, prompt: polishPreview.after }))
      setPolishPreview(null)
      toast.success('Prompt polished', 'The improved content has been applied. Review and save.')
    }
  }

  const handleSaveAsPrompt = async (): Promise<void> => {
    if (!form.prompt.trim()) {
      toast.error('Nothing to save', 'Write or generate a prompt first.')
      return
    }
    setIsSavingAsPrompt(true)
    try {
      const promptSlug = `${form.slug || 'orchestrator'}-prompt`
      const promptName = `${form.name.trim() || form.slug || 'Orchestrator'} Prompt`
      const created = await api.post<{ id: string }>('/admin/ai/prompts', {
        slug: promptSlug,
        name: promptName,
        description: `Classification prompt for ${form.name.trim() || form.slug || 'orchestrator'}`,
        category: 'orchestrator',
        content: form.prompt.trim(),
      })
      setForm((prev) => ({ ...prev, promptId: created.id, prompt: '' }))
      setPromptSource('template')
      // Refresh the available prompts list so the new template appears
      api.get<{ prompts: { id: string; slug: string; name: string; content: string; category: string }[] }>('/admin/ai/prompts').then((res) => {
        setAvailablePrompts(res.prompts ?? [])
      }).catch(() => {})
      toast.success('Saved to Prompt Studio', 'The prompt is now a reusable template. Save the orchestrator to apply.')
    } catch (err) {
      toast.error('Failed to save prompt', err instanceof Error ? err.message : String(err))
    } finally {
      setIsSavingAsPrompt(false)
    }
  }

  // Live validation drives both the inline errors and the Save button's
  // disabled state, so the user never round-trips to a 400.
  const liveErrors = useMemo(() => validateForm(form, !!editing), [form, editing])
  const isValid = Object.keys(liveErrors).length === 0

  // Dirty-state: compare form and members against their initial
  // snapshots. Save is disabled when nothing has changed (matching the
  // EditAgentDrawer pattern).
  const isDirty = useMemo(() => {
    const formChanged = JSON.stringify(form) !== JSON.stringify(initialForm)
    const membersChanged = JSON.stringify(members) !== JSON.stringify(initialMembers)
    return formChanged || membersChanged
  }, [form, initialForm, members, initialMembers])

  const validate = (): boolean => {
    setErrors(liveErrors)
    return isValid
  }

  const executeSave = async (): Promise<void> => {
    setSaving(true)
    try {
      const payload = {
        slug: form.slug,
        name: form.name,
        description: form.description || null,
        purpose: form.purpose,
        prompt: promptSource === 'inline' ? form.prompt : '',
        promptId: promptSource === 'template' ? form.promptId : null,
        type: form.type,
        parentOrchestratorId: form.type === 'sub-orchestrator' ? form.parentOrchestratorId || null : null,
        config: form.config,
        isEnabled: form.isEnabled,
        sortOrder: form.sortOrder,
        changeNote: form.changeNote || null,
        updatedAt: form.updatedAt,
      }

      let responseUpdatedAt: string = form.updatedAt ?? new Date().toISOString()
      const bumpTimestamp = (ts: string | undefined): void => {
        if (ts) {
          responseUpdatedAt = ts
          setForm((prev) => ({ ...prev, updatedAt: responseUpdatedAt }))
        }
      }
      if (editing) {
        const updated = await api.put<{ orchestrator: Orchestrator }>(`/admin/ai/orchestrators/${editing.slug}`, payload)
        bumpTimestamp(updated.orchestrator.updatedAt)
      } else {
        const created = await api.post<{ orchestrator: Orchestrator }>('/admin/ai/orchestrators', payload)
        bumpTimestamp(created.orchestrator.updatedAt)
      }

      // Attach member agents for side orchestrators.
      if (form.type === 'sub-orchestrator') {
        const targetSlug = editing ? editing.slug : form.slug
        const selectedAgents = members
          .filter((m) => agents.some((a) => a.id === m.agentId))
          .map((m) => ({
            agentId: m.agentId,
            isEnabled: m.isEnabled,
            sortOrder: m.sortOrder,
            intentFilter: m.intentFilter,
          }))
        const agentsRes = await api.put<{ updatedAt?: string }>(`/admin/ai/orchestrators/${targetSlug}/agents`, { agents: selectedAgents, changeNote: form.changeNote || null })
        bumpTimestamp(agentsRes.updatedAt)
      }

      toast.success(editing ? 'Orchestrator updated' : 'Orchestrator created')
      await onSaved()
    } catch (err: unknown) {
      const errObj = err as {
        status?: number
        data?: {
          error?: string
          message?: string
          orchestrator?: Orchestrator
          conflicts?: Array<{ agent: string; assignedTo: string; assignedToSlug: string }>
        }
      }
      if (errObj.status === 409 && errObj.data?.orchestrator) {
        setConflictServerRecord(errObj.data.orchestrator)
        // Sync the form's updatedAt from the server so the user can retry
        // without reloading the page.
        const serverUpdatedAt = errObj.data.orchestrator.updatedAt
        if (serverUpdatedAt) setForm((prev) => ({ ...prev, updatedAt: serverUpdatedAt }))
      } else if (errObj.status === 409 && errObj.data?.error === 'DUPLICATE_SLUG') {
        toast.error('Slug already in use', errObj.data.message ?? 'An orchestrator with this slug already exists.')
      } else {
        toast.error('Failed to save orchestrator:', err instanceof Error ? err.message : String(err))
      }
    } finally {
      setSaving(false)
    }
  }

  const handleSave = async (): Promise<void> => {
    if (!validate()) return

    // Main demotion check
    if (form.type === 'main' && form.isEnabled) {
      const activeMain = orchestrators.find((o) => o.type === 'main' && o.isEnabled && !o.isArchived && o.id !== editing?.id)
      if (activeMain) {
        setShowDemoteConfirm(true)
        return
      }
    }

    await executeSave()
  }

  const addAgent = (agent: AgentOption): void => {
    setMembers((prev) => {
      if (prev.some((m) => m.agentId === agent.id)) return prev
      return [
        ...prev,
        {
          id: `new-${agent.id}`,
          agentId: agent.id,
          slug: agent.slug,
          name: agent.name,
          type: agent.type,
          isEnabled: true,
          sortOrder: prev.length,
          intentFilter: [],
        },
      ]
    })
  }

  const removeMember = (agentId: string): void => {
    setMembers((prev) =>
      prev.filter((m) => m.agentId !== agentId).map((m, i) => ({ ...m, sortOrder: i })),
    )
  }

  const updateMember = (agentId: string, patch: Partial<OrchestratorMember>): void => {
    setMembers((prev) => prev.map((m) => (m.agentId === agentId ? { ...m, ...patch } : m)))
  }

  /** Moves a member to a new index and recomputes every `sortOrder`. */
  const reorderMembers = (from: number | null, to: number): void => {
    if (from === null || from === to) return
    setMembers((prev) => {
      if (from < 0 || from >= prev.length || to < 0 || to >= prev.length) return prev
      const next = [...prev]
      const [moved] = next.splice(from, 1)
      if (!moved) return prev
      next.splice(to, 0, moved)
      return next.map((m, i) => ({ ...m, sortOrder: i }))
    })
  }

  const unassignedAgents = useMemo(
    () => agents.filter((a) => !members.some((m) => m.agentId === a.id)),
    [agents, members],
  )

  const enabledMemberCount = members.filter((m) => m.isEnabled).length

  const currentParentName = useMemo(() => {
    if (editing?.parentOrchestratorId) {
      return orchestrators.find((o) => o.id === editing.parentOrchestratorId)?.name ?? 'Main Orchestrator'
    }
    return mains[0]?.name ?? 'Main Orchestrator'
  }, [editing, orchestrators, mains])

  if (!isOpen) return <></>

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <div
        className="absolute right-0 top-0 flex h-full w-full flex-col border-l border-gray-200 bg-white shadow-2xl md:w-[640px]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-100 p-4">
          <div className="flex items-center gap-2">
            <Pencil className="h-4 w-4 text-gray-500" />
            <h2 className="text-sm font-semibold text-gray-900">
              {editing ? 'Edit Orchestrator' : 'Create Orchestrator'}
            </h2>
          </div>
          <button
            className="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
            onClick={onClose}
            aria-label="Close drawer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* ── Tab navigation ── */}
        <div className="flex border-b border-gray-200 px-4">
          {([
            { key: 'details' as const, label: 'Details', icon: <Settings className="h-3.5 w-3.5" /> },
            { key: 'prompt' as const, label: 'Prompt', icon: <FileText className="h-3.5 w-3.5" /> },
            { key: 'agents' as const, label: 'Agents', icon: <Users className="h-3.5 w-3.5" /> },
          ]).map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 border-b-2 px-3 py-2.5 text-xs font-semibold transition-colors ${
                activeTab === tab.key
                  ? 'border-[#3454cf] text-[#3454cf]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.icon}
              {tab.label}
              {tab.key === 'agents' && form.type === 'sub-orchestrator' && enabledMemberCount > 0 && (
                <span className="ml-0.5 rounded-full bg-[#3454cf]/10 px-1.5 text-[9px] font-bold text-[#3454cf]">
                  {enabledMemberCount}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto p-4 pb-28">
          {/* ── Tab 1: Details ── */}
          {activeTab === 'details' && (
            <>
              <div>
                <FieldLabel label="Name" value={form.name} max={FIELD_LIMITS.name.max} />
                <input
                  className={inputClass(!!errors.name)}
                  value={form.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                />
                {errors.name && <p className="mt-1 text-[10px] text-red-500">{errors.name}</p>}
              </div>

              <div>
                <FieldLabel label="Slug" value={form.slug} max={FIELD_LIMITS.slug.max} />
                <input
                  className={inputClass(!!errors.slug, !!editing)}
                  value={form.slug}
                  disabled={!!editing}
                  title={editing ? 'Slug cannot be changed after creation — it is used as a stable identifier by routing logic and tests.' : undefined}
                  onChange={(e) => {
                    setIsSlugDirty(true)
                    setForm((p) => ({ ...p, slug: e.target.value }))
                  }}
                />
                {errors.slug && <p className="mt-1 text-[10px] text-red-500">{errors.slug}</p>}
              </div>

              <div>
                <label className={labelClass}>Type</label>
                <Select
                  ariaLabel="Orchestrator type"
                  value={form.type}
                  disabled={!!editing}
                  onChange={(v) => setForm((p) => ({ ...p, type: v as 'main' | 'sub-orchestrator' }))}
                >
                  <option value="sub-orchestrator">Sub-Orchestrator</option>
                  <option value="main">Main</option>
                </Select>
                {editing && (
                  <p className="mt-1 text-[11px] text-gray-400">Type cannot be changed after creation — it determines the orchestrator hierarchy (main vs sub-orchestrator).</p>
                )}
              </div>

              {form.type === 'sub-orchestrator' && (
                <div>
                  <label className={labelClass}>Parent Orchestrator</label>
                  <div className="rounded-sm border border-gray-200 bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-700 flex items-center gap-2">
                    <Network className="h-3.5 w-3.5 text-blue-600" />
                    <span>Parent: {currentParentName}</span>
                  </div>
                </div>
              )}

              <div>
                <FieldLabel label="Description" value={form.description} max={FIELD_LIMITS.description.max} />
                <textarea
                  className={`${inputClass(!!errors.description)} min-h-[70px]`}
                  value={form.description}
                  onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                />
                {errors.description && <p className="mt-1 text-[10px] text-red-500">{errors.description}</p>}
              </div>

              <div>
                <FieldLabel label="Purpose" value={form.purpose} max={FIELD_LIMITS.purpose.max} />
                <textarea
                  className={`${inputClass(!!errors.purpose)} min-h-[70px]`}
                  value={form.purpose}
                  onChange={(e) => setForm((p) => ({ ...p, purpose: e.target.value }))}
                  placeholder="Why does this orchestrator exist? What outcome is it optimizing for?"
                />
                {errors.purpose && <p className="mt-1 text-[10px] text-red-500">{errors.purpose}</p>}
                <p className="mt-1 text-[10px] text-gray-400">
                  The outcome this orchestrator is ultimately optimizing for. Distinct from description (which explains what it does).
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>Sort Order</label>
                  <input
                    type="number"
                    className={inputClass()}
                    value={form.sortOrder}
                    onChange={(e) => setForm((p) => ({ ...p, sortOrder: Number(e.target.value) }))}
                  />
                </div>
                <div className="flex items-end">
                  <label className="flex cursor-pointer items-center gap-2">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-gray-300 text-[#3454cf] focus:ring-[#3454cf]"
                      checked={form.isEnabled}
                      onChange={(e) => setForm((p) => ({ ...p, isEnabled: e.target.checked }))}
                    />
                    <span className="text-xs text-gray-700">Enabled</span>
                  </label>
                </div>
              </div>

              <div>
                <label className={labelClass}>Audit Change Note (Optional)</label>
                <input
                  className={inputClass()}
                  value={form.changeNote}
                  onChange={(e) => setForm((p) => ({ ...p, changeNote: e.target.value }))}
                  placeholder="e.g. Updated system prompt for Q4 campaign..."
                />
              </div>
            </>
          )}

          {/* ── Tab 2: Prompt ── */}
          {activeTab === 'prompt' && (
            <div className="space-y-4">
              <div>
                <h4 className="mb-1 text-sm font-semibold text-gray-900">Classification Prompt</h4>
                <p className="text-xs text-gray-500">
                Optional custom instruction preamble for this orchestrator&apos;s LLM classification call.
                Leave empty to use the built-in classifier prompt — routing behaviour is unchanged until you write one.
                </p>
              </div>

              {/* Prompt source toggle — identical to EditAgentDrawer */}
              <div
                role="radiogroup"
                aria-label="Prompt source"
                className="mb-2 grid grid-cols-2 gap-1 rounded-sm bg-gray-100 p-1"
              >
                {([
                  { value: 'inline', label: 'Write inline', icon: <FileText className="h-3 w-3" /> },
                  { value: 'template', label: 'Use a template', icon: <Bot className="h-3 w-3" /> },
                ] as const).map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    role="radio"
                    aria-checked={promptSource === opt.value}
                    onClick={() => handlePromptSourceChange(opt.value)}
                    className={`inline-flex items-center justify-center gap-1.5 rounded-sm px-2 py-1.5 text-xs font-semibold transition-colors ${
                      promptSource === opt.value
                        ? 'bg-white text-[#3454cf] shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {opt.icon}
                    {opt.label}
                  </button>
                ))}
              </div>

              {/* Inline prompt mode */}
              {promptSource === 'inline' ? (
                <div>
                  {/* Guardrail selection — available when writing an inline
                      prompt, whether manually or via AI generation. */}
                  <div className="mb-3">
                    <GuardrailPicker
                      selectedIds={selectedGuardrailIds}
                      onChange={(ids, contents) => {
                        setSelectedGuardrailIds(ids)
                        setSelectedGuardrailContents(contents)
                      }}
                    />
                  </div>
                  <div className="mb-1 flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setGenBrief(emptyBrief())
                      setShowGenerate((v) => !v)
                    }}
                    className="inline-flex items-center gap-1 text-[10px] font-medium text-[#3454cf] hover:underline"
                  >
                    <Sparkles className="h-3 w-3" />
                    {showGenerate ? 'Hide' : 'Generate with AI'}
                  </button>
                  <button
                    type="button"
                    onClick={() => void handlePolish()}
                    disabled={isPolishing || form.prompt.trim().length < 5}
                    className="inline-flex items-center gap-1 text-[10px] font-medium text-[#3454cf] hover:underline disabled:opacity-40"
                  >
                    {isPolishing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />}
                    Polish with AI
                  </button>
                </div>

                {/* AI generate panel */}
                {showGenerate && (
                  <div className="mb-3 rounded-sm border border-[#3454cf]/20 bg-[#3454cf]/5 p-3">
                    <div className="space-y-3">
                      <div>
                        <label className={labelClass}>What should this prompt do?</label>
                        <textarea
                          value={genBrief.task}
                          onChange={(e) => setGenBrief((p) => ({ ...p, task: e.target.value }))}
                          rows={2}
                          className={`${inputClass()} resize-none`}
                          placeholder="e.g. Classify each user message and route it to the correct sub-orchestrator"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className={labelClass}>Audience</label>
                          <input
                            type="text"
                            value={genBrief.audience}
                            onChange={(e) => setGenBrief((p) => ({ ...p, audience: e.target.value }))}
                            className={inputClass()}
                            placeholder="e.g. Support platform"
                          />
                        </div>
                        <div>
                          <label className={labelClass}>Tone</label>
                          <Select
                            ariaLabel="Tone"
                            value={genBrief.tone}
                            onChange={(v) => setGenBrief((p) => ({ ...p, tone: v as GenerateBrief['tone'] }))}
                          >
                            {TONE_OPTIONS.map((t) => (
                              <option key={t.value} value={t.value}>
                                {t.label}
                              </option>
                            ))}
                          </Select>
                        </div>
                      </div>
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => setShowGenerate(false)}
                          className="rounded-sm border border-gray-200 px-3 py-1.5 text-[10px] font-medium text-gray-600 transition-colors hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={() => void handleGenerate()}
                          disabled={isGenerating || !genBrief.task.trim()}
                          className="inline-flex items-center gap-1 rounded-sm bg-[#3454cf] px-3 py-1.5 text-[10px] font-semibold text-white transition-colors hover:bg-[#2a45b0] disabled:opacity-50"
                        >
                          {isGenerating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                          Generate
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Polish preview — before/after with accept/reject */}
                {polishPreview && (
                  <div className="mb-3 rounded-sm border border-[#3454cf]/20 bg-[#3454cf]/5 p-3">
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-[#3454cf]">Polish preview</span>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setPolishPreview(null)}
                          className="rounded-sm border border-gray-200 px-3 py-1.5 text-[10px] font-medium text-gray-600 transition-colors hover:bg-gray-50"
                        >
                          Reject
                        </button>
                        <button
                          type="button"
                          onClick={handleAcceptPolish}
                          className="inline-flex items-center gap-1 rounded-sm bg-[#3454cf] px-3 py-1.5 text-[10px] font-semibold text-white transition-colors hover:bg-[#2a45b0]"
                        >
                          <Check className="h-3 w-3" />
                          Accept
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">Before</p>
                        <pre className="max-h-48 overflow-auto rounded-sm bg-white p-2 text-[10px] text-gray-600 whitespace-pre-wrap">{polishPreview.before}</pre>
                      </div>
                      <div>
                        <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">After</p>
                        <pre className="max-h-48 overflow-auto rounded-sm bg-white p-2 text-[10px] text-gray-900 whitespace-pre-wrap">{polishPreview.after}</pre>
                      </div>
                    </div>
                  </div>
                )}

                <textarea
                  className={`${inputClass(!!errors.prompt)} min-h-[220px] resize-none font-mono`}
                  value={form.prompt}
                  onChange={(e) => setForm((p) => ({ ...p, prompt: e.target.value }))}
                  placeholder="Leave empty to use the built-in classifier prompt."
                />
                {errors.prompt && <p className="mt-1 text-[10px] text-red-500">{errors.prompt}</p>}
                <div className="mt-1.5 flex items-center justify-between">
                  <p className="text-[10px] text-gray-400">
                    This prompt is sent to the AI model on every classification call.
                  </p>
                  <button
                    type="button"
                    onClick={() => void handleSaveAsPrompt()}
                    disabled={isSavingAsPrompt || !form.prompt.trim()}
                    className="inline-flex items-center gap-1 rounded bg-[#3454cf] px-2.5 py-1 text-[10px] font-medium text-white hover:bg-[#2a45b0] disabled:opacity-50"
                  >
                    {isSavingAsPrompt ? <Loader2 className="h-3 w-3 animate-spin" /> : <BookOpen className="h-3 w-3" />}
                    {isSavingAsPrompt ? 'Saving…' : 'Save to Prompt Studio'}
                  </button>
                </div>
                </div>
              ) : (
                /* Template picker — identical to EditAgentDrawer */
                <div className="space-y-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={promptSearch}
                      onChange={(e) => setPromptSearch(e.target.value)}
                      placeholder="Search prompt templates..."
                      className={`${inputClass()} pl-9`}
                    />
                  </div>
                  <div className="max-h-80 space-y-1 overflow-y-auto">
                    {filteredPrompts.length === 0 ? (
                      <p className="py-4 text-center text-xs text-gray-400">No prompt templates found.</p>
                    ) : (
                      filteredPrompts.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => handlePromptSelect(p.id)}
                          className={`w-full rounded-md border px-3 py-2 text-left transition-colors ${
                            form.promptId === p.id
                              ? 'border-[#3454cf] bg-[#3454cf]/5'
                              : 'border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-gray-900">{p.name}</span>
                            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[9px] font-medium text-gray-500">{p.category}</span>
                          </div>
                          <p className="mt-0.5 line-clamp-2 text-[10px] text-gray-500">{p.content}</p>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Tab 3: Agents ── */}
          {activeTab === 'agents' && (
            <>
              {form.type === 'sub-orchestrator' ? (
                <div className="border-t border-gray-100 pt-4">
                  <h3 className="text-sm font-semibold text-gray-900">Member Agents</h3>
                  <p className="mb-2 text-[10px] text-gray-400">
                    Drag the handle to set dispatch priority — the order here is the order agents are tried.
                  </p>

                  {members.length === 0 ? (
                    <p className="rounded-sm border border-dashed border-gray-200 px-3 py-4 text-center text-[11px] text-gray-400">
                      No agents assigned yet. Add one from the list below.
                    </p>
                  ) : (
                    <ul className="space-y-2">
                      {members.map((member, index) => (
                        <li
                          key={member.agentId}
                          draggable
                          onDragStart={() => setDragIndex(index)}
                          onDragEnd={() => {
                            setDragIndex(null)
                            setDragOverIndex(null)
                          }}
                          onDragOver={(e) => {
                            e.preventDefault()
                            setDragOverIndex(index)
                          }}
                          onDrop={(e) => {
                            e.preventDefault()
                            reorderMembers(dragIndex, index)
                            setDragIndex(null)
                            setDragOverIndex(null)
                          }}
                          className={`rounded-sm border bg-white p-3 ${
                            dragOverIndex === index && dragIndex !== index
                              ? 'border-[#3454cf] ring-1 ring-[#3454cf]'
                              : 'border-gray-200'
                          } ${dragIndex === index ? 'opacity-50' : ''}`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex min-w-0 items-center gap-2">
                              <span
                                className="cursor-grab text-gray-400 hover:text-gray-600 active:cursor-grabbing"
                                title="Drag to reorder"
                                aria-hidden="true"
                              >
                                <GripVertical className="h-3.5 w-3.5" />
                              </span>
                              <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-600">
                                #{index + 1}
                              </span>
                              <div className="min-w-0">
                                <div className="truncate text-xs font-medium text-gray-900">{member.name}</div>
                                <div className="truncate text-[10px] text-gray-400">{member.slug}</div>
                              </div>
                            </div>
                            <div className="flex shrink-0 items-center gap-1">
                              {/* Keyboard-accessible equivalent of dragging. */}
                              <button
                                type="button"
                                onClick={() => reorderMembers(index, index - 1)}
                                disabled={index === 0}
                                className="rounded border border-gray-200 p-1 text-gray-500 hover:bg-gray-100 disabled:opacity-30"
                                aria-label={`Move ${member.name} up`}
                              >
                                <ArrowUp className="h-3 w-3" />
                              </button>
                              <button
                                type="button"
                                onClick={() => reorderMembers(index, index + 1)}
                                disabled={index === members.length - 1}
                                className="rounded border border-gray-200 p-1 text-gray-500 hover:bg-gray-100 disabled:opacity-30"
                                aria-label={`Move ${member.name} down`}
                              >
                                <ArrowDown className="h-3 w-3" />
                              </button>
                              <button
                                type="button"
                                className="ml-1 rounded p-1 text-red-500 hover:bg-red-50"
                                onClick={() => removeMember(member.agentId)}
                                aria-label={`Remove ${member.name}`}
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          </div>
                          <div className="mt-2 pl-6">
                            <label className="mb-0.5 block text-[10px] text-gray-400">Intent Filter Tags</label>
                            <TagInput
                              tags={member.intentFilter}
                              onChange={(tags) => updateMember(member.agentId, { intentFilter: tags })}
                              suggestions={getIntentFilterSuggestions(member.type)}
                            />
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}

                  {unassignedAgents.length > 0 && (
                    <div className="mt-3">
                      <label className={labelClass}>Add an agent</label>
                      <div className="flex flex-wrap gap-1.5">
                        {unassignedAgents.map((agent) => (
                          <button
                            key={agent.id}
                            type="button"
                            onClick={() => addAgent(agent)}
                            className="inline-flex items-center gap-1 rounded-sm border border-gray-200 bg-white px-2 py-1 text-[11px] text-gray-700 hover:border-[#3454cf] hover:bg-blue-50/40"
                          >
                            <Plus className="h-3 w-3" />
                            {agent.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="rounded-sm border border-blue-100 bg-blue-50/60 p-3 text-[11px] leading-relaxed text-blue-900">
                  <div className="mb-1 flex items-center gap-1.5">
                    <Network className="h-3.5 w-3.5 text-blue-600" />
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-blue-700">Main Orchestrator</span>
                  </div>
                  <p>
                    Main orchestrators don't have member agents directly — they route to sub-orchestrators, which in turn dispatch to member agents.
                    Configure member agents on each sub-orchestrator instead.
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        <div className="absolute bottom-0 left-0 right-0 border-t border-gray-200 bg-white p-4">
          <div className="flex items-center justify-between gap-3">
            {activeTab === 'agents' ? (
              <button
                type="button"
                onClick={() => setActiveTab('details')}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
              >
                <ArrowDown className="h-3.5 w-3.5 rotate-[90deg]" />
                Back: Details
              </button>
            ) : (
              <span />
            )}
            <div className="flex items-center gap-3">
              <button
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
                onClick={onClose}
              >
                Cancel
              </button>
              {activeTab === 'details' ? (
                <button
                  type="button"
                  onClick={() => setActiveTab('agents')}
                  disabled={!isValid}
                  className={`inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 ${
                    isValid
                      ? 'bg-[#3454cf] hover:bg-[#2a45b0]'
                      : 'cursor-not-allowed bg-gray-300'
                  }`}
                  title={!isValid ? 'Fix the highlighted validation errors first' : undefined}
                >
                  Next: Agents
                  <ArrowDown className="h-4 w-4 rotate-[-90deg]" />
                </button>
              ) : (
                <button
                  className={`inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 ${
                    isDirty && isValid
                      ? 'bg-[#3454cf] hover:bg-[#2a45b0]'
                      : 'cursor-not-allowed bg-gray-300'
                  }`}
                  onClick={() => void handleSave()}
                  disabled={saving || !isValid || !isDirty}
                  title={!isValid ? 'Fix the highlighted validation errors first' : !isDirty ? 'No changes to save' : undefined}
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  {saving ? 'Saving…' : 'Save Changes'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Prompt Source Switch Confirmation */}
      {pendingSourceChange && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
            <div className="mb-3 flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              <h3 className="text-sm font-bold text-gray-900">
                Switch to {pendingSourceChange.source === 'template' ? 'a prompt template' : 'an inline prompt'}?
              </h3>
            </div>
            <p className="mb-4 text-xs leading-relaxed text-gray-600">
              An orchestrator can only use one prompt source, so switching will discard{' '}
              <strong>{pendingSourceChange.discarding}</strong>. The previous prompt is preserved in the audit history and can be restored later.
              Are you sure you want to continue?
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setPendingSourceChange(null)}
                className="rounded border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  pendingSourceChange.apply()
                  setPendingSourceChange(null)
                }}
                className="rounded bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700"
              >
                Yes, switch
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Prompt Template Override Confirmation */}
      {pendingPromptChange && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
            <div className="mb-3 flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              <h3 className="text-sm font-bold text-gray-900">
                {pendingPromptChange.nextName ? 'Override the existing prompt?' : 'Remove the assigned prompt?'}
              </h3>
            </div>
            <p className="mb-3 text-xs leading-relaxed text-gray-600">
              An orchestrator can only use one prompt template at a time.
              {pendingPromptChange.nextName
                ? ' Assigning a new template will replace the one currently assigned.'
                : ' Clearing the selection will unassign the current template.'}
            </p>
            <div className="mb-4 space-y-1.5 rounded bg-gray-50 p-3 text-xs">
              <div className="flex items-center gap-2">
                <span className="w-14 shrink-0 text-[10px] uppercase text-gray-400">Current</span>
                <span className="min-w-0 truncate font-medium text-red-700 line-through">
                  {pendingPromptChange.previousName}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-14 shrink-0 text-[10px] uppercase text-gray-400">New</span>
                <span className="min-w-0 truncate font-medium text-emerald-700">
                  {pendingPromptChange.nextName ?? 'None'}
                </span>
              </div>
            </div>
            <p className="mb-4 text-xs font-medium text-gray-700">
              Are you sure you want to assign it like this?
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setPendingPromptChange(null)}
                className="rounded border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setForm((prev) => ({ ...prev, promptId: pendingPromptChange.promptId || null }))
                  setPendingPromptChange(null)
                }}
                className="rounded bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700"
              >
                Yes, override
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Demotion Confirmation Dialog */}
      {showDemoteConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
            <div className="flex items-center gap-2 text-amber-600 mb-3">
              <AlertTriangle className="h-5 w-5" />
              <h3 className="text-sm font-bold text-gray-900">Demote Active Main Orchestrator?</h3>
            </div>
            <p className="text-xs text-gray-600 leading-relaxed mb-4">
              Another main orchestrator is currently enabled. Enabling this one as main will demote the existing main orchestrator to a sub-orchestrator underneath it.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowDemoteConfirm(false)}
                className="rounded border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  setShowDemoteConfirm(false)
                  await executeSave()
                }}
                className="rounded bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700"
              >
                Demote & Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Conflict Resolution Dialog (Optimistic Locking) */}
      {conflictServerRecord && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-lg bg-white p-5 shadow-xl">
            <div className="flex items-center gap-2 text-red-600 mb-3">
              <AlertCircle className="h-5 w-5" />
              <h3 className="text-sm font-bold text-gray-900">Concurrent Modification Conflict</h3>
            </div>
            <p className="text-xs text-gray-600 leading-relaxed mb-4">
              This orchestrator was updated by another user after you opened it. Reloading will update your drawer with the latest server data.
            </p>
            <div className="rounded bg-gray-50 p-3 text-xs font-mono space-y-1 mb-4">
              <div>Server Record: <strong>{conflictServerRecord.name}</strong></div>
              <div>Last Updated: {new Date(conflictServerRecord.updatedAt).toLocaleString()}</div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConflictServerRecord(null)}
                className="rounded border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel Edits
              </button>
              <button
                onClick={() => {
                  const s = conflictServerRecord
                  setForm({
                    slug: s.slug,
                    name: s.name,
                    description: s.description ?? '',
                    purpose: s.purpose ?? '',
                    prompt: s.prompt ?? '',
                    promptId: s.promptId ?? null,
                    type: s.type,
                    parentOrchestratorId: s.parentOrchestratorId ?? '',
                    configPreset: presetFromConfig(s.config),
                    config: s.config ?? null,
                    isEnabled: s.isEnabled,
                    sortOrder: s.sortOrder,
                    changeNote: '',
                    updatedAt: s.updatedAt,
                  })
                  setPromptSource(s.promptId ? 'template' : 'inline')
                  setConflictServerRecord(null)
                  toast.info('Form reloaded with latest server version')
                }}
                className="rounded bg-[#3454cf] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#2a45b0]"
              >
                Reload Latest Version
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Component: DiffViewer
// ---------------------------------------------------------------------------

interface MembersDiffPayload {
  added?: Array<{ name: string; sortOrder: number; intentFilter: string[] }>
  removed?: Array<{ name: string }>
  changed?: Array<{ name: string; changes: Record<string, { before: unknown; after: unknown }> }>
}

function formatDiffValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

function DiffRow({ field, before, after }: { field: string; before: unknown; after: unknown }): JSX.Element {
  return (
    <div className="grid grid-cols-[110px_1fr_1fr] gap-2 border-t border-gray-100 py-1.5 first:border-t-0">
      <span className="font-semibold text-gray-500">{field}</span>
      <span className="break-words rounded bg-red-50 px-1.5 py-0.5 font-mono text-[10px] text-red-800 line-through">
        {formatDiffValue(before)}
      </span>
      <span className="break-words rounded bg-emerald-50 px-1.5 py-0.5 font-mono text-[10px] text-emerald-800">
        {formatDiffValue(after)}
      </span>
    </div>
  )
}

/**
 * Renders a history diff. Handles both the field-level shape
 * (`{ field: { before, after } }`) and the membership shape
 * (`{ members: { added, removed, changed } }`).
 */
function DiffViewer({ diff }: { diff: Record<string, { before: unknown; after: unknown }> }): JSX.Element {
  const entries = Object.entries(diff ?? {})
  if (entries.length === 0) {
    return <p className="py-2 text-[11px] italic text-gray-400">No field changes recorded.</p>
  }

  const membersEntry = diff.members as unknown as MembersDiffPayload | undefined
  const isMembersDiff =
    !!membersEntry &&
    ('added' in membersEntry || 'removed' in membersEntry || 'changed' in membersEntry)

  if (isMembersDiff) {
    const { added = [], removed = [], changed = [] } = membersEntry
    if (added.length === 0 && removed.length === 0 && changed.length === 0) {
      return <p className="py-2 text-[11px] italic text-gray-400">Membership saved with no effective changes.</p>
    }
    return (
      <div className="space-y-2 text-[11px]">
        {added.length > 0 && (
          <div>
            <span className="font-semibold text-emerald-700">Added</span>
            <ul className="mt-0.5 space-y-0.5">
              {added.map((m) => (
                <li key={m.name} className="rounded bg-emerald-50 px-2 py-1 text-emerald-900">
                  + {m.name}
                  {m.intentFilter.length > 0 && (
                    <span className="ml-1 font-mono text-[10px] text-emerald-700">[{m.intentFilter.join(', ')}]</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
        {removed.length > 0 && (
          <div>
            <span className="font-semibold text-red-700">Removed</span>
            <ul className="mt-0.5 space-y-0.5">
              {removed.map((m) => (
                <li key={m.name} className="rounded bg-red-50 px-2 py-1 text-red-900">
                  − {m.name}
                </li>
              ))}
            </ul>
          </div>
        )}
        {changed.length > 0 && (
          <div>
            <span className="font-semibold text-blue-700">Changed</span>
            <div className="mt-0.5 space-y-1">
              {changed.map((m) => (
                <div key={m.name} className="rounded border border-gray-200 p-2">
                  <div className="mb-1 font-semibold text-gray-800">{m.name}</div>
                  {Object.entries(m.changes).map(([field, v]) => (
                    <DiffRow key={field} field={field} before={v.before} after={v.after} />
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="text-[11px]">
      <div className="grid grid-cols-[110px_1fr_1fr] gap-2 pb-1 text-[10px] font-semibold uppercase text-gray-400">
        <span>Field</span>
        <span>Before</span>
        <span>After</span>
      </div>
      {entries.map(([field, value]) => (
        <DiffRow key={field} field={field} before={value?.before} after={value?.after} />
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Drawer: History & Audit Log
// ---------------------------------------------------------------------------

function OrchestratorHistoryDrawerModal({
  isOpen,
  orchestrator,
  onClose,
  onRestored,
}: {
  isOpen: boolean
  orchestrator: Orchestrator | null
  onClose: () => void
  onRestored: () => void
}): JSX.Element {
  const toast = useToast()
  const [history, setHistory] = useState<HistoryEntry[]>([])
  const [loading, setLoading] = useState(false)
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const fetchHistory = useCallback(async () => {
    if (!orchestrator) return
    setLoading(true)
    try {
      const res = await api.get<{ history: HistoryEntry[] }>(`/admin/ai/orchestrators/${orchestrator.slug}/history`)
      setHistory(res.history ?? [])
    } catch (err) {
      toast.error('Failed to load history:', err instanceof Error ? err.message : String(err))
    } finally {
      setLoading(false)
    }
  }, [orchestrator, toast])

  useEffect(() => {
    if (isOpen) void fetchHistory()
  }, [isOpen, fetchHistory])

  const handleRestore = async (historyId: string) => {
    if (!orchestrator) return
    try {
      await api.post(`/admin/ai/orchestrators/${orchestrator.slug}/history/${historyId}/restore`, {})
      toast.success('Orchestrator state restored from history snapshot')
      onRestored()
    } catch (err) {
      toast.error('Failed to restore:', err instanceof Error ? err.message : String(err))
    }
  }

  if (!isOpen || !orchestrator) return <></>

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <div
        className="absolute right-0 top-0 flex h-full w-full flex-col border-l border-gray-200 bg-white shadow-2xl md:w-[540px]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <div className="flex items-center gap-2">
            <HistoryIcon className="h-4 w-4 text-[#3454cf]" />
            <h2 className="text-sm font-semibold text-gray-900">Audit History: {orchestrator.name}</h2>
          </div>
          <button className="rounded-sm p-1.5 text-gray-400 hover:bg-gray-100" onClick={onClose}>
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3 text-xs">
          {loading ? (
            <div className="py-8 text-center text-gray-400">Loading audit history...</div>
          ) : history.length === 0 ? (
            <div className="py-8 text-center text-gray-400">No audit history recorded yet.</div>
          ) : (
            history.map((entry) => {
              const isExpanded = expandedId === entry.id
              return (
                <div key={entry.id} className="rounded border border-gray-200 bg-white p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span
                        className={`rounded px-2 py-0.5 text-[10px] font-bold uppercase ${
                          entry.action === 'create'
                            ? 'bg-emerald-100 text-emerald-800'
                            : entry.action === 'archive'
                              ? 'bg-amber-100 text-amber-800'
                              : entry.action === 'restore'
                                ? 'bg-purple-100 text-purple-800'
                                : 'bg-blue-100 text-blue-800'
                        }`}
                      >
                        {entry.action}
                      </span>
                      <span className="font-semibold text-gray-900">{entry.changedByName}</span>
                    </div>
                    <span className="text-[10px] text-gray-400">{new Date(entry.changedAt).toLocaleString()}</span>
                  </div>

                  {entry.changeNote && (
                    <p className="text-gray-600 bg-gray-50 p-2 rounded italic text-[11px]">
                      "{entry.changeNote}"
                    </p>
                  )}

                  <div className="flex items-center justify-between pt-1">
                    <button
                      onClick={() => setExpandedId(isExpanded ? null : entry.id)}
                      className="text-[11px] font-medium text-blue-600 hover:underline flex items-center gap-1"
                    >
                      <FileText className="h-3 w-3" />
                      {isExpanded ? 'Hide Diff' : 'View Diff'}
                    </button>
                    {entry.action !== 'create' && (
                      <button
                        onClick={() => void handleRestore(entry.id)}
                        className="inline-flex items-center gap-1 text-[11px] font-medium text-purple-700 bg-purple-50 px-2 py-1 rounded hover:bg-purple-100"
                      >
                        <RotateCcw className="h-3 w-3" />
                        Restore Snapshot
                      </button>
                    )}
                  </div>

                  {isExpanded && (
                    <div className="mt-2 overflow-x-auto rounded border border-gray-200 bg-gray-50/60 p-3">
                      <DiffViewer diff={entry.diff} />
                    </div>
                  )}
                </div>
              )
            })
          )}
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Modal: Delete Safety with Dependency Checks
// ---------------------------------------------------------------------------

function DeleteSafetyModal({
  isOpen,
  orchestrator,
  orchestrators,
  onClose,
  onDeleted,
}: {
  isOpen: boolean
  orchestrator: Orchestrator | null
  orchestrators: Orchestrator[]
  onClose: () => void
  onDeleted: () => void
}): JSX.Element {
  const toast = useToast()
  const [confirmSlug, setConfirmSlug] = useState('')
  const [deleting, setDeleting] = useState(false)
  const [historyCount, setHistoryCount] = useState<number | null>(null)

  const dependentSides = useMemo(() => {
    if (!orchestrator || orchestrator.type !== 'main') return []
    return orchestrators.filter((o) => o.parentOrchestratorId === orchestrator.id && !o.isArchived)
  }, [orchestrator, orchestrators])

  const dependentAgentsCount = orchestrator?.members?.length ?? 0
  const hasDependents = dependentSides.length > 0 || dependentAgentsCount > 0

  useEffect(() => {
    if (!isOpen || !orchestrator) return
    setConfirmSlug('')
    setHistoryCount(null)
    api
      .get<{ total: number }>(`/admin/ai/orchestrators/${orchestrator.slug}/history?limit=1`)
      .then((res) => setHistoryCount(res.total ?? 0))
      .catch(() => setHistoryCount(null))
  }, [isOpen, orchestrator])

  if (!isOpen || !orchestrator) return <></>

  const handleDelete = async () => {
    if (confirmSlug.trim() !== orchestrator.slug) return
    setDeleting(true)
    try {
      await api.delete(`/admin/ai/orchestrators/${orchestrator.slug}`)
      toast.success('Orchestrator removed')
      onDeleted()
    } catch (err: unknown) {
      const errObj = err as { data?: { message?: string } }
      toast.error('Cannot delete:', errObj.data?.message || (err instanceof Error ? err.message : String(err)))
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl space-y-4">
        <div className="flex items-start justify-between border-b border-gray-100 pb-3">
          <div className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="h-5 w-5" />
            <h3 className="text-sm font-bold text-gray-900">Delete Orchestrator</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-4 w-4" />
          </button>
        </div>

        {hasDependents ? (
          <div className="space-y-3">
            <div className="rounded bg-red-50 p-3 text-xs text-red-800 font-medium leading-relaxed">
              Cannot delete <strong>{orchestrator.name}</strong>. Active resources are linked to it:
            </div>
            {dependentSides.length > 0 && (
              <div className="text-xs text-gray-700">
                <span className="font-semibold text-gray-900">Dependent Sub-Orchestrators:</span>
                <ul className="mt-1 list-disc pl-5 space-y-0.5 font-mono text-[11px] text-gray-600">
                  {dependentSides.map((s) => (
                    <li key={s.id}>{s.name} ({s.slug})</li>
                  ))}
                </ul>
              </div>
            )}
            {dependentAgentsCount > 0 && (
              <div className="text-xs text-gray-700">
                <span className="font-semibold text-gray-900">Assigned Member Agents:</span> {dependentAgentsCount} agents
              </div>
            )}
            <p className="text-xs text-gray-500 italic">Please reassign or remove dependent resources before deleting.</p>
            <div className="pt-2 flex justify-end">
              <button
                onClick={onClose}
                className="rounded bg-gray-100 px-4 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {historyCount !== null && historyCount > 0 && (
              <p className="rounded-sm border border-amber-200 bg-amber-50 px-2.5 py-2 text-[11px] leading-snug text-amber-800">
                This orchestrator has <strong>{historyCount}</strong> audit history{' '}
                {historyCount === 1 ? 'entry' : 'entries'}. Deleting will permanently remove it
                and all associated history. This cannot be undone.
              </p>
            )}
            {historyCount !== null && historyCount === 0 && (
              <p className="rounded-sm border border-red-200 bg-red-50 px-2.5 py-2 text-[11px] leading-snug text-red-800">
                This orchestrator has no audit history, so it will be{' '}
                <strong>permanently deleted</strong>. This cannot be undone.
              </p>
            )}
            <p className="text-xs text-gray-600">
              Type the orchestrator slug <strong className="font-mono text-red-700 bg-red-50 px-1 py-0.5 rounded">{orchestrator.slug}</strong> below to confirm deletion:
            </p>
            <input
              value={confirmSlug}
              onChange={(e) => setConfirmSlug(e.target.value)}
              placeholder={orchestrator.slug}
              className="w-full rounded border border-gray-300 px-3 py-2 text-xs font-mono outline-none focus:border-red-500"
            />
            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={onClose}
                className="rounded border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                disabled={confirmSlug.trim() !== orchestrator.slug || deleting}
                onClick={() => void handleDelete()}
                className="inline-flex items-center gap-1.5 rounded bg-red-600 px-4 py-1.5 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-40"
              >
                {deleting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Confirm Delete
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
